import { type FC, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, type InjectedFormProps, reduxForm } from "redux-form";

import { formatNumberByThousands } from "widgets/utils";

import { useGetFormError } from "../../../app-utils/hooks/useForm.js";
import { type InputRangeInput, type Project, type SampleType } from "../../../graphql.js";
import { getProjectLabel } from "../../../redux/modules/config.js";
import { updateNfPlaybookExecutionState } from "../../../redux/modules/nf-playbook-execution.js";
import { type IAppState } from "../../../redux/reducer.js";
import { type ReduxDispatch } from "../../../redux/redux-utils.js";
import {
  MAX_LENGTH,
  MAX_PLAYBOOK_RANGE_LENGTH,
  NF_PLAYBOOK_SCREEN_IDS,
  PLAYBOOK_TYPE,
} from "../../../settings/settings.js";
import ProjectsSelect from "../../core/forms/ProjectsSelect.js";
import InputField, { InputReduxFormField } from "../../core/redux-form/InputField.js";
import { FieldTitle } from "../FieldTitle.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../PlaybookWizardStep.js";

type INfPlaybookBasicsScreenProps = IPlaybookWizardStepProvidedProps &
  InjectedFormProps<FormData, any> &
  IPlaybookWizardStepInjectedProps;

const FIELD_PROJECT = "project";
const FIELD_EVENT_NAME = "EVENT_NAME";
const FIELD_EVENT_DESCRIPTION = "EVENT_DESCRIPTION";

const NfPlaybookBasicsScreen: FC<INfPlaybookBasicsScreenProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const globalFormError = useGetFormError(NF_PLAYBOOK_SCREEN_IDS.NF_BASICS_SCREEN);
  const selectedProject = useSelector<IAppState, Project>((state) => state.nfPlaybookExecution.project);
  const sampleType = useSelector<IAppState, SampleType>((state) => state.sampleEditingState.sampleType);
  const eventName = useSelector<IAppState, string>((state) => state.nfPlaybookExecution.eventName);
  const eventDescription = useSelector<IAppState, string>((state) => state.nfPlaybookExecution.eventDescription);
  const contentNotAvailableWarning = useSelector<IAppState, string>(
    (state) => state.nfPlaybookExecution.contentUnavailableWarning,
  );
  const playbookTypeId = useSelector<IAppState, PLAYBOOK_TYPE>((state) => state.nfPlaybookExecution.playbook.type.id);
  const reportRange = useSelector<IAppState, InputRangeInput>(
    (state) => state.nfPlaybookExecution.reportTableQuerySpec?.restrictions?.region?.range,
  );

  useEffect(() => {
    props.initialize({
      [FIELD_PROJECT]: selectedProject,
      [FIELD_EVENT_NAME]: eventName,
      [FIELD_EVENT_DESCRIPTION]: eventDescription,
    });
  }, []);

  const handleSubmitData = useCallback((values) => {
    const selectedProject: Project = values[FIELD_PROJECT];
    const eventName: string = values[FIELD_EVENT_NAME];
    const eventDescription: string = values[FIELD_EVENT_DESCRIPTION];
    dispatch(
      updateNfPlaybookExecutionState({
        eventName,
        eventDescription,
        project: selectedProject,
      }),
    );
    props.proceedWithNextStep();
  }, []);

  if (playbookTypeId === PLAYBOOK_TYPE.ALIGNMENT) {
    const rangeSize = reportRange.stop - reportRange.start + 1;
    if (rangeSize >= MAX_PLAYBOOK_RANGE_LENGTH) {
      props.updateWizardMessage(
        `You must provide a range that is less than 10 kb in size (current size is ${formatNumberByThousands(rangeSize)} bases)`,
      );
    }
  }

  return (
    <PlaybookWizardStep
      {...props}
      playbookPage={{
        ...props.playbookPage,
      }}
      wizardFooterData={{
        error: globalFormError || props.error,
        warning: contentNotAvailableWarning,
        nextButtonProps: {
          label: "Next",
          isLoading: !!props.asyncValidating,
          onPress: props.handleSubmit(handleSubmitData),
        },
      }}
    >
      <Form className="form-horizontal">
        <ProjectsSelect
          name={FIELD_PROJECT}
          horizontalLayout
          sampleTypeId={sampleType?.id}
          title={<FieldTitle label={getProjectLabel(true, false)} mandatory />}
          tooltip={`Select the ${getProjectLabel(false, false)} to which the analysis and its output will be attached`}
          onError={(error: Error) => props.updateWizardMessage(error.message)}
        />
        <InputReduxFormField
          horizontalLayout
          key={"event-name"}
          name={FIELD_EVENT_NAME}
          label={<FieldTitle label={"Event name"} />}
          component={InputField}
          placeholder="Enter text..."
          maxLength={MAX_LENGTH.EVENT.NAME}
          tooltip={"You can provide a name for the playbook event"}
        />
        <InputReduxFormField
          horizontalLayout
          key={"event-description"}
          name={FIELD_EVENT_DESCRIPTION}
          label={<FieldTitle label={"Event description"} />}
          component={InputField}
          placeholder="Enter text..."
          maxLength={MAX_LENGTH.EVENT.DESCRIPTION}
          tooltip={"You can provide a description for the playbook event"}
        />
      </Form>
    </PlaybookWizardStep>
  );
};

export default reduxForm<any, IPlaybookWizardStepProvidedProps>({
  form: NF_PLAYBOOK_SCREEN_IDS.NF_BASICS_SCREEN,
  destroyOnUnmount: false,
})(NfPlaybookBasicsScreen);
